export type ReportWidgetSlotRole = 'title' | 'primaryValue' | 'subtitle' | 'secondaryValue' | 'badge' | 'color';

export interface ReportWidgetSlotDefinition {
  key: string;
  role: string;
  labelKey: string;
  label?: string;
  required?: boolean;
}

export interface ReportWidgetConfigurableArea {
  key: string;
  role?: ReportWidgetSlotRole | string;
  labelKey?: string;
  label?: string;
  required?: boolean;
}

export interface ReportWidgetTemplateCapabilities {
  chart: boolean;
  summary: boolean;
  calculations: boolean;
  styleRules: boolean;
  comparison: boolean;
}

export interface ReportWidgetRankBadgeToneConfig {
  enabled?: boolean;
  shadePercent?: number;
}

export interface ReportWidgetHeaderConfig {
  title?: string;
  subtitle?: string;
}

export interface ReportWidgetTemplateDefinition {
  templateKey: string;
  rendererKey: string;
  visualVariant: string;
  slots: ReportWidgetSlotRole[];
  capabilities: ReportWidgetTemplateCapabilities;
}

export interface ReportWidgetTemplateConfig {
  version?: number;
  presetKey?: string;
  templateKey?: string;
  rendererKey?: string;
  visualVariant?: string;
  supportedSlots?: ReportWidgetSlotRole[];
  configurableAreas?: ReportWidgetConfigurableArea[];
  displayColumns?: string[];
  rowLimit?: number;
  highlightFirst?: boolean;
  hideLabels?: boolean;
  slotBindings?: Record<string, string>;
  calculations?: ReportWidgetCalculationConfig[];
  conditionalRules?: ReportWidgetRuleConfig[];
  styleRules?: ReportWidgetRuleConfig[];
  formatting?: Record<string, unknown>;
  rankBadgeTone?: ReportWidgetRankBadgeToneConfig;
  header?: ReportWidgetHeaderConfig;
  chart?: ReportWidgetChartConfig;
  capabilities?: Partial<ReportWidgetTemplateCapabilities>;
}

export interface ReportWidgetCalculationConfig {
  id?: string;
  label?: string;
  scope?: 'row' | 'summary' | 'style' | string;
  expression?: string;
  format?: string;
  visible?: boolean;
}

export interface ReportWidgetRuleConfig {
  id?: string;
  target?: string;
  targetType?: string;
  targetKey?: string;
  decoration?: string;
  position?: string;
  source?: string;
  operator?: string;
  value?: string;
  valueTo?: string;
  textColor?: string;
  backgroundColor?: string;
  textColorSource?: string;
  backgroundColorSource?: string;
  colorMode?: string;
  colorPalette?: string[];
  colorPaletteMode?: string;
  colorPaletteSource?: string;
  icon?: string;
  badgeLabel?: string;
  severity?: string;
  priority?: number;
  templateGenerated?: boolean;
  sourceTemplateKey?: string;
  templateRuleId?: string;
}

export interface ReportWidgetChartConfig {
  enabled?: boolean;
  type?: string;
  argumentField?: string;
  valueField?: string;
  valueFields?: string[];
  seriesMode?: string;
  aggregation?: string;
  rotated?: boolean;
  legendVisible?: boolean;
  labelsVisible?: boolean;
  palette?: string;
  comparison?: {
    enabled?: boolean;
    mode?: string;
    period?: string;
    labelKey?: string;
  };
}

export interface ReportWidgetRenderData {
  widgetId?: string;
  reportId?: string;
  reportName?: string;
  reportGroupKey?: string;
  displayRowCount?: number;
  templateKey?: string;
  templateConfigJson?: string;
  headers?: string[];
  rows?: Record<string, any>[];
  summary?: Record<string, any>;
  slots?: Record<string, any>;
  metadata?: Record<string, any>;
  errors?: string[];
  refreshedAt?: string;
}

export const REPORT_WIDGET_SLOT_DEFINITIONS: Record<ReportWidgetSlotRole, ReportWidgetSlotDefinition> = {
  title: {
    key: 'title',
    role: 'title',
    labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.SlotRoleLabels.Title'
  },
  primaryValue: {
    key: 'primaryValue',
    role: 'primaryValue',
    labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.SlotRoleLabels.Primary'
  },
  subtitle: {
    key: 'subtitle',
    role: 'subtitle',
    labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.SlotRoleLabels.Secondary'
  },
  secondaryValue: {
    key: 'secondaryValue',
    role: 'secondaryValue',
    labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.SlotRoleLabels.Extra'
  },
  badge: {
    key: 'badge',
    role: 'badge',
    labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.SlotRoleLabels.Badge'
  },
  color: {
    key: 'color',
    role: 'color',
    labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.SlotRoleLabels.Color'
  }
};

const DEFAULT_CAPABILITIES: ReportWidgetTemplateCapabilities = {
  chart: false,
  summary: false,
  calculations: true,
  styleRules: true,
  comparison: false
};

export const REPORT_WIDGET_TEMPLATE_DEFINITIONS: ReportWidgetTemplateDefinition[] = [
  {
    templateKey: 'top-list',
    rendererKey: 'top-list',
    visualVariant: 'ranked',
    slots: ['title', 'primaryValue'],
    capabilities: { ...DEFAULT_CAPABILITIES }
  },
  {
    templateKey: 'top-one',
    rendererKey: 'top-one',
    visualVariant: 'spotlight',
    slots: ['title', 'primaryValue', 'subtitle', 'secondaryValue', 'badge'],
    capabilities: { ...DEFAULT_CAPABILITIES }
  },
  {
    templateKey: 'compact-table',
    rendererKey: 'compact-table',
    visualVariant: 'table',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES }
  },
  {
    templateKey: 'kpi-cards',
    rendererKey: 'kpi-cards',
    visualVariant: 'kpi',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES, summary: true }
  },
  {
    templateKey: 'alert-threshold-list',
    rendererKey: 'alert-threshold-list',
    visualVariant: 'threshold-list',
    slots: ['title', 'primaryValue', 'badge'],
    capabilities: { ...DEFAULT_CAPABILITIES }
  },
  {
    templateKey: 'dx-bar-chart',
    rendererKey: 'dx-bar-chart',
    visualVariant: 'category-bar',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES, chart: true, comparison: true }
  },
  {
    templateKey: 'dx-horizontal-bar-chart',
    rendererKey: 'dx-horizontal-bar-chart',
    visualVariant: 'ranking',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES, chart: true, comparison: true }
  },
  {
    templateKey: 'dx-line-chart',
    rendererKey: 'dx-line-chart',
    visualVariant: 'line-trend',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES, chart: true, comparison: true }
  },
  {
    templateKey: 'dx-area-chart',
    rendererKey: 'dx-area-chart',
    visualVariant: 'sensor-area',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES, chart: true, comparison: true }
  },
  {
    templateKey: 'dx-pie-chart',
    rendererKey: 'dx-pie-chart',
    visualVariant: 'status-pie',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES, chart: true }
  },
  {
    templateKey: 'dx-donut-chart',
    rendererKey: 'dx-donut-chart',
    visualVariant: 'status-donut',
    slots: [],
    capabilities: { ...DEFAULT_CAPABILITIES, chart: true }
  }
];

export function parseReportWidgetTemplateConfig(configJson?: string | null): ReportWidgetTemplateConfig {
  if (!configJson || !String(configJson).trim()) {
    return {};
  }

  try {
    return JSON.parse(configJson) || {};
  } catch {
    return {};
  }
}

export function isReportWidgetChartRenderer(rendererKey?: string | null): boolean {
  const value = String(rendererKey || '').trim();
  return value.startsWith('dx-') && value.includes('chart');
}

export function getReportWidgetTemplateDefinition(
  templateKey?: string | null,
  rendererKey?: string | null,
  config?: ReportWidgetTemplateConfig | null
): ReportWidgetTemplateDefinition {
  const keys = [
    config?.templateKey,
    config?.presetKey,
    templateKey,
    config?.rendererKey,
    rendererKey
  ]
    .map((item) => String(item || '').trim())
    .filter((item) => item.length > 0);

  for (const key of keys) {
    const match = REPORT_WIDGET_TEMPLATE_DEFINITIONS.find((definition) =>
      definition.templateKey === key || definition.rendererKey === key
    );
    if (match) {
      return {
        ...match,
        capabilities: {
          ...match.capabilities,
          ...(config?.capabilities || {})
        }
      };
    }
  }

  const resolvedRenderer = keys.find((key) => key.length > 0) || 'top-list';
  const chart = isReportWidgetChartRenderer(resolvedRenderer);
  return {
    templateKey: resolvedRenderer,
    rendererKey: resolvedRenderer,
    visualVariant: config?.visualVariant || resolvedRenderer,
    slots: chart || resolvedRenderer === 'compact-table' || resolvedRenderer === 'kpi-cards'
      ? []
      : ['title', 'primaryValue'],
    capabilities: {
      ...DEFAULT_CAPABILITIES,
      chart,
      summary: resolvedRenderer === 'kpi-cards',
      comparison: ['dx-bar-chart', 'dx-horizontal-bar-chart', 'dx-line-chart', 'dx-area-chart'].includes(resolvedRenderer),
      ...(config?.capabilities || {})
    }
  };
}

export function getReportWidgetSlotDefinitions(
  templateKey?: string | null,
  rendererKey?: string | null,
  config?: ReportWidgetTemplateConfig | null
): ReportWidgetSlotDefinition[] {
  return getReportWidgetConfigurableAreas(templateKey, rendererKey, config);
}

export function getReportWidgetConfigurableAreas(
  templateKey?: string | null,
  rendererKey?: string | null,
  config?: ReportWidgetTemplateConfig | null
): ReportWidgetSlotDefinition[] {
  const definition = getReportWidgetTemplateDefinition(templateKey, rendererKey, config);
  const configuredAreas = Array.isArray(config?.configurableAreas)
    ? config.configurableAreas
    : [];
  if (configuredAreas.length) {
    return normalizeConfigurableAreas(configuredAreas);
  }

  const slots = Array.isArray(config?.supportedSlots) && config.supportedSlots.length
    ? config.supportedSlots
    : definition.slots;
  return normalizeConfigurableAreas(slots.map((role) => ({ key: role, role })));
}

function normalizeConfigurableAreas(areas: ReportWidgetConfigurableArea[]): ReportWidgetSlotDefinition[] {
  const usedKeys = new Set<string>();
  const normalized: ReportWidgetSlotDefinition[] = [];
  areas.forEach((area) => {
    const key = String(area?.key || area?.role || '').trim();
    if (!key || usedKeys.has(key)) {
      return;
    }

    usedKeys.add(key);
    const role = String(area?.role || key).trim() || key;
    const knownSlot = REPORT_WIDGET_SLOT_DEFINITIONS[role as ReportWidgetSlotRole];
    normalized.push({
      key,
      role,
      labelKey: area?.labelKey || knownSlot?.labelKey || 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.SlotRoleLabels.Unknown',
      label: area?.label,
      required: area?.required === true
    });
  });

  return normalized;
}
